// Per-hour stat decay rates (stats are 0–100)
const DECAY_RATES = {
  energy:    2.0,  // depletes fully in ~50 hrs from 100 without studying
  happiness: 1.5,  // depletes fully in ~67 hrs from 100 without logging in
  fullness:  3.0,  // depletes fully in ~33 hrs from 100 without feeding
};

// Boosts applied on various activities
export const STUDY_BOOST = { energy: 15, happiness: 10, fullness: 0 };
export const GAME_BOOST  = { energy:  8, happiness:  5, fullness: 0 };
export const LOGIN_BOOST = { energy:  0, happiness:  8, fullness: 0 };

// Food items purchasable with coins in the Care tab
export const FOOD_ITEMS = [
  {
    id: 'apple',
    name: 'Apple',
    emoji: '🍎',
    cost: 50,
    effects: { energy: 0, happiness: 0, fullness: 30 },
    description: 'A healthy snack',
  },
  {
    id: 'studysnack',
    name: 'Study Snack',
    emoji: '🍪',
    cost: 75,
    effects: { energy: 0, happiness: 5, fullness: 35 },
    description: 'Brain fuel',
  },
  {
    id: 'coffee',
    name: 'Coffee',
    emoji: '☕',
    cost: 100,
    effects: { energy: 20, happiness: 5, fullness: 20 },
    description: 'Medical student essential',
  },
  {
    id: 'energydrink',
    name: 'Energy Drink',
    emoji: '⚡',
    cost: 150,
    effects: { energy: 35, happiness: 0, fullness: 15 },
    description: 'Extreme boost',
  },
  {
    id: 'pizza',
    name: 'Pizza',
    emoji: '🍕',
    cost: 200,
    effects: { energy: 0, happiness: 10, fullness: 50 },
    description: 'A big treat',
  },
];

const clamp = (v) => Math.min(100, Math.max(0, Math.round(v)));

export const defaultAvatarStats = () => ({
  energy: 80,
  happiness: 80,
  fullness: 80,
  lastUpdated: null,
});

/**
 * Apply time-based decay to avatar stats based on time elapsed since lastUpdated.
 * Returns new stats object with updated lastUpdated timestamp.
 */
export const decayStats = (stats, lastUpdated) => {
  const now = new Date();
  if (!lastUpdated) {
    return { ...stats, lastUpdated: now.toISOString() };
  }
  const hoursElapsed = (now.getTime() - new Date(lastUpdated).getTime()) / (1000 * 60 * 60);
  const h = Math.min(hoursElapsed, 48); // cap so very long absences aren't catastrophic
  return {
    energy:    clamp((stats.energy    ?? 80) - DECAY_RATES.energy    * h),
    happiness: clamp((stats.happiness ?? 80) - DECAY_RATES.happiness * h),
    fullness:  clamp((stats.fullness  ?? 80) - DECAY_RATES.fullness  * h),
    lastUpdated: now.toISOString(),
  };
};

/**
 * Apply a boost object to current stats, clamping each to 0–100.
 */
export const applyBoost = (stats, boost) => ({
  energy:    clamp((stats?.energy    ?? 80) + (boost.energy    || 0)),
  happiness: clamp((stats?.happiness ?? 80) + (boost.happiness || 0)),
  fullness:  clamp((stats?.fullness  ?? 80) + (boost.fullness  || 0)),
  lastUpdated: new Date().toISOString(),
});

/**
 * Return eyebrows + mouth override and a CSS filter class driven by avatar stats.
 *
 * Logic:
 *  - CSS filter is based on the overall average (how "healthy" the avatar looks)
 *  - Expression is based on which individual stat is lowest (tells player what to fix)
 *
 * Required images in /public/:
 *   avatar-eyebrows-droopy.png      (tired, half-closed)
 *   avatar-eyebrows-worried.png     (inner brows raised, concerned)
 *   avatar-eyebrows-reallyangry.png (crisis state)
 *   avatar-mouth-frown.png          (sad downward curve)
 *   avatar-mouth-hungry.png         (wavy / growling)
 */
export const getAvatarMoodFromStats = (stats) => {
  if (!stats) return { eyebrows: 'happy', mouth: 'teeth', filterClass: '' };

  const energy    = stats.energy    ?? 80;
  const happiness = stats.happiness ?? 80;
  const fullness  = stats.fullness  ?? 80;
  const avg = (energy + happiness + fullness) / 3;

  // Overall visual filter (gets greyer / dimmer as average drops)
  let filterClass = '';
  if      (avg < 15) filterClass = 'saturate-0 brightness-75';
  else if (avg < 30) filterClass = 'saturate-50 brightness-90';
  else if (avg < 50) filterClass = 'saturate-50';

  // All stats are great → happy face
  if (avg >= 75) return { eyebrows: 'happy', mouth: 'teeth', filterClass };

  // Still doing well overall → neutral/studious
  if (avg >= 60) return { eyebrows: 'thick', mouth: null, filterClass };

  // Crisis: everything very low → really angry / exhausted
  if (avg < 15) return { eyebrows: 'reallyangry', mouth: 'frown', filterClass };

  // Find which stat is lowest to drive the stat-specific expression
  const minVal = Math.min(energy, happiness, fullness);
  const lowestStat =
    minVal === energy    ? 'energy' :
    minVal === fullness  ? 'fullness' : 'happiness';

  if (lowestStat === 'energy') {
    // Tired / low energy
    return {
      eyebrows: 'droopy',
      mouth: minVal < 30 ? 'frown' : null,
      filterClass,
    };
  }

  if (lowestStat === 'fullness') {
    // Hungry
    return {
      eyebrows: minVal < 30 ? 'angry' : 'worried',
      mouth: 'hungry',
      filterClass,
    };
  }

  // Happiness is lowest → sad
  return {
    eyebrows: minVal < 30 ? 'angry' : 'worried',
    mouth: 'frown',
    filterClass,
  };
};

/** Returns a Tailwind bg-color class for a stat progress bar. */
export const getStatColor = (value) => {
  if (value >= 60) return 'bg-green-500';
  if (value >= 30) return 'bg-yellow-400';
  return 'bg-red-500';
};

/** Returns a short urgency label. */
export const getStatLabel = (value) => {
  if (value >= 60) return '';
  if (value >= 30) return 'Low';
  return 'Critical!';
};
